/**
 * Deciding the tempo of a dragged file.
 *
 * A file dragged out of the Sounds browser with Transpose & Warp on is not the
 * catalog file — the plugin re-rendered it to the session tempo. Four things
 * claim to know its tempo, and they can disagree:
 *
 * - the host, which states the tempo outright on the drag payload when it is
 *   new enough to do so (`DraggedAssetTransform`). Nothing beats being told.
 * - the audio in hand, whose duration says what actually happened to it
 * - the render suffix in the filename (`modified-file-name.ts`), which is the
 *   plugin declaring what it rendered to
 * - the catalog asset it came from, which describes the *original* audio
 *
 * Bar count survives warping — stretching a 4-bar loop leaves it 4 bars long —
 * so the duration ratio recovers the tempo without trusting anyone's naming:
 *
 *     renderedBpm = catalogBpm × catalogDuration / renderedDuration
 *
 * That derivation is what we lean on once the host has said nothing, because it
 * describes the file rather than describing an intention. It also self-checks:
 * an untouched file gives a ratio of 1 and hands the catalog tempo straight
 * back. It still matters with the payload shipped — plugin builds reach users
 * on their own release cadence, so an older host sends no transform at all, and
 * the web surface has no host to ask.
 *
 * `streams.transport.bpm` is deliberately not a source. It only helps for a
 * file we already know was warped, which is the case the others cover, and on
 * the web surface it is the user's own transport rather than a session tempo —
 * right-looking and meaningless.
 */

/** Where a resolved tempo came from, for the caller to reason about trust. */
export type BpmSource = "reported" | "catalog" | "declared" | "derived";

export interface ResolvedBpm {
    bpm: number | null;
    source: BpmSource | undefined;
}

export interface DraggedBpmInputs {
    /** Tempo the host stated on the drag payload. Outranks everything else. */
    reportedBpm: number | undefined;
    /** Tempo the catalog holds for the asset this file came from. */
    catalogBpm: number | undefined;
    /** Duration the catalog holds for that asset, in milliseconds. */
    catalogDuration_ms: number | undefined;
    /** Duration of the audio actually delivered, in seconds. */
    renderedDuration_s: number | undefined;
    /** Tempo the plugin declared in the filename, when it modified the file. */
    declaredBpm: number | undefined;
    /**
     * Only loops warp predictably. A repeated one-shot is longer than the
     * catalog says for reasons that have nothing to do with tempo, and a
     * one-shot has no tempo worth reporting anyway.
     */
    isLoop: boolean;
}

/** Two tempos this close describe the same audio; further apart, they conflict. */
const kAgreementRatio = 0.02;

/**
 * Widest warp we treat as real. Beyond this the duration gap is more likely a
 * trimmed or repeated file, or a catalog duration that was never right, than a
 * tempo change — so the derivation is dropped rather than believed.
 */
const kMaxWarpRatio = 4;

const agrees = (a: number, b: number): boolean => Math.abs(a - b) <= b * kAgreementRatio;

/** Tempos land on hundredths; DAWs report fractional tempos, so don't round to whole. */
const roundBpm = (bpm: number): number => Math.round(bpm * 100) / 100;

const deriveBpm = ({
    catalogBpm,
    catalogDuration_ms,
    renderedDuration_s,
    isLoop,
}: DraggedBpmInputs): number | undefined => {
    if (!isLoop || catalogBpm === undefined || catalogBpm <= 0) {
        return undefined;
    }
    if (catalogDuration_ms === undefined || catalogDuration_ms <= 0) {
        return undefined;
    }
    if (renderedDuration_s === undefined || renderedDuration_s <= 0) {
        return undefined;
    }

    const ratio = catalogDuration_ms / 1000 / renderedDuration_s;
    if (ratio > kMaxWarpRatio || ratio < 1 / kMaxWarpRatio) {
        return undefined;
    }
    return catalogBpm * ratio;
};

/**
 * Resolve the tempo to record for a dragged file.
 *
 * The host's own value wins outright when present. Below that: where the
 * derivation and the filename agree, the filename wins the tie: it
 * carries the session tempo as the DAW reported it (`140`), where the
 * derivation lands a hair off it (`139.98`). Where they disagree, the
 * derivation wins — a timestretch multiplier renders half-time audio into a
 * 140 BPM session, and 70 is the tempo that matches its transients.
 */
export const resolveDraggedBpm = (inputs: DraggedBpmInputs): ResolvedBpm => {
    const { reportedBpm, catalogBpm, declaredBpm } = inputs;

    // The host computed this from the transform it just applied. There is
    // nothing to infer past it.
    if (reportedBpm !== undefined && reportedBpm > 0) {
        return { bpm: reportedBpm, source: "reported" };
    }

    const derived = deriveBpm(inputs);

    if (derived === undefined) {
        if (declaredBpm !== undefined) {
            return { bpm: declaredBpm, source: "declared" };
        }
        return {
            bpm: catalogBpm ?? null,
            source: catalogBpm === undefined ? undefined : "catalog",
        };
    }

    if (declaredBpm !== undefined && agrees(derived, declaredBpm)) {
        return { bpm: declaredBpm, source: "declared" };
    }

    // Nothing was declared and the file is the length the catalog says it is,
    // so it arrived untouched — keep the catalog's own number rather than a
    // derivation of it, which would read as 127.98.
    if (declaredBpm === undefined && catalogBpm !== undefined && agrees(derived, catalogBpm)) {
        return { bpm: catalogBpm, source: "catalog" };
    }

    return { bpm: roundBpm(derived), source: "derived" };
};
